import { RecordQ } from "@pdfking/structs"

import { Keys } from "../functions/Keys"
import { RecordCreate } from "../functions/RecordCreate"
import { Values } from "../functions/Values"

/**
 * Waits for all promises to resolve, preserving the input shape.
 * Accepts either an array of promises or a record of promises.
 *
 * @param promises - Array or record of promises to wait for
 * @returns A promise resolving to an array or record of the resolved values
 *
 * @example
 * ```ts
 * import { WaitPromises } from "@pdfking/tfunc"
 *
 * await WaitPromises([Promise.resolve(1), Promise.resolve("two")])
 * // [1, "two"]
 *
 * await WaitPromises({ one: Promise.resolve(1), two: Promise.resolve("two") })
 * // { one: 1, two: "two" }
 * ```
 */
export function WaitPromises<T extends Readonly<unknown[]> | []>(
  promises: T
): Promise<{ -readonly [K in keyof T]: Awaited<T[K]> }>
export function WaitPromises<T extends Record<string, Promise<unknown>>>(
  promises: T
): Promise<{ [K in keyof T]: Awaited<T[K]> }>
export function WaitPromises<T>(promises: Iterable<Promise<T>>): Promise<T[]>
export async function WaitPromises(
  promises: Readonly<unknown[]> | Record<string, Promise<unknown>> | Iterable<Promise<unknown>>
): Promise<unknown[] | Record<string, unknown>> {
  if (RecordQ(promises)) {
    return RecordCreate(Keys(promises), await Promise.all(Values(promises)))
  }

  return await Promise.all(promises)
}
